import { useCallback, useEffect, useRef, type RefObject } from "react";
import { CALENDAR_DRAWER_TRANSITION_MS } from "../drawer/CalendarDrawer";

/** Breathing room kept between a revealed event and the grid's edge. */
export const REVEAL_MARGIN_PX = 16;

interface RevealGeometry {
  /** The event's left / right edges (viewport px). */
  itemLeft: number;
  itemRight: number;
  /** The visible part of the scroller (viewport px), sticky gutter excluded. */
  viewLeft: number;
  viewRight: number;
  margin?: number;
}

/**
 * Horizontal scroll delta (px) that brings an event fully into the visible
 * part of the grid, or 0 when it already is. A clipped right edge wins: the
 * event lands just left of the drawer (but never past the left edge, so an
 * event wider than the view aligns its left edge instead).
 */
export function revealScrollDelta({
  itemLeft,
  itemRight,
  viewLeft,
  viewRight,
  margin = REVEAL_MARGIN_PX,
}: RevealGeometry): number {
  const rightOverflow = itemRight + margin - viewRight;
  if (rightOverflow > 0) {
    return Math.min(rightOverflow, itemLeft - margin - viewLeft);
  }
  const leftOverflow = viewLeft + margin - itemLeft;
  return leftOverflow > 0 ? -leftOverflow : 0;
}

/** Width of the time gutter when RBC pins it (resource views), else 0. */
const stickyGutterWidth = (scroller: HTMLElement): number => {
  const gutter = scroller.querySelector<HTMLElement>(".rbc-time-gutter");
  if (!gutter || getComputedStyle(gutter).position !== "sticky") return 0;
  return gutter.getBoundingClientRect().width;
};

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Scroll the grid (RBC's .rbc-time-content) so `el` is fully visible. */
export function revealInGrid(el: HTMLElement): void {
  const scroller = el.closest<HTMLElement>(".rbc-time-content");
  if (!scroller) return;
  const item = el.getBoundingClientRect();
  const view = scroller.getBoundingClientRect();
  const delta = revealScrollDelta({
    itemLeft: item.left,
    itemRight: item.right,
    viewLeft: view.left + stickyGutterWidth(scroller),
    // clientWidth leaves out the vertical scrollbar.
    viewRight: view.left + scroller.clientWidth,
  });
  if (delta === 0) return;
  scroller.scrollBy({
    left: delta,
    behavior: prefersReducedMotion() ? "auto" : "smooth",
  });
}

/**
 * How long to wait for the push transition's `transitionend` before
 * revealing anyway (the transition may not run, or may start late behind a
 * heavy render).
 */
const PUSH_FALLBACK_MS = CALENDAR_DRAWER_TRANSITION_MS + 300;

/**
 * Day / Week grid: keep a clicked event next to the drawer it opens. The grid
 * reflows beside an open drawer (CalendarView's margin-right push on
 * `pushRef`), so with more columns than fit, an event near the right edge
 * slides out of view; once the push settles, the grid scrolls just enough to
 * bring it back. An event that is still fully visible (left / middle of the
 * grid) never moves.
 *
 * Returns the click hook: pass it the clicked event's element. It only acts
 * when a drawer is open after the push, and the grid stays put when the
 * drawer closes.
 */
export function useRevealBesideDrawer(
  drawerOpen: boolean,
  pushRef: RefObject<HTMLElement | null>,
) {
  const drawerOpenRef = useRef(drawerOpen);
  useEffect(() => {
    drawerOpenRef.current = drawerOpen;
  }, [drawerOpen]);

  // Cancels the pending reveal (a newer click, or unmount).
  const cancelRef = useRef<(() => void) | null>(null);
  useEffect(() => () => cancelRef.current?.(), []);

  return useCallback(
    (target: EventTarget | null | undefined) => {
      cancelRef.current?.();
      cancelRef.current = null;
      if (!(target instanceof HTMLElement)) return;
      const reveal = () => {
        cancelRef.current?.();
        cancelRef.current = null;
        if (drawerOpenRef.current && target.isConnected) revealInGrid(target);
      };

      // A drawer that's already open has already pushed the grid.
      if (drawerOpenRef.current) {
        const frame = window.requestAnimationFrame(reveal);
        cancelRef.current = () => window.cancelAnimationFrame(frame);
        return;
      }

      // A new drawer pushes the grid over its open transition.
      const push = pushRef.current;
      const onPushEnd = (e: TransitionEvent) => {
        if (e.target === push && e.propertyName === "margin-right") reveal();
      };
      push?.addEventListener("transitionend", onPushEnd);
      const fallback = window.setTimeout(reveal, PUSH_FALLBACK_MS);
      cancelRef.current = () => {
        push?.removeEventListener("transitionend", onPushEnd);
        window.clearTimeout(fallback);
      };
    },
    [pushRef],
  );
}
